import React, { useEffect, useState } from 'react';
import apiService from '../services/apiService';
import {
  FormRow,
  FormWrapper,
  Label,
  SubmitButton,
  CancelButton,
} from '../styles/ComponentStyles';
import {
  StatusSelect,
  BillDateInput,
  BillNumberInput,
  InstallmentSection,
  HelperText,
} from '../styles/BillsFormStyles';
import {
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
} from '../styles/SubscriptionFormModalStyles';

// Fallback list mirrors the backend enum; replaced by /get_options once loaded.
const DEFAULT_TYPES = [
  'Stocks',
  'Mutual Fund/UITF',
  'Crypto',
  'Bonds',
  'Real Estate',
  'Business',
  'Other',
];

const inputStyle = {
  width: '100%',
  maxWidth: 400,
  padding: '10px 12px',
  borderRadius: 6,
  border: '1px solid #555',
  backgroundColor: '#2e2e2e',
  color: '#e0e0e0',
};

const InvestmentFormModal = ({ editing, initialData, onSubmit, onClose }) => {
  const [investmentTypes, setInvestmentTypes] = useState(DEFAULT_TYPES);
  const [formData, setFormData] = useState({
    name: '',
    investment_type: 'Stocks',
    custom_type: '',
    platform: '',
    remarks: '',
    initial_amount: '',
    start_date: '',
  });

  useEffect(() => {
    let active = true;
    apiService
      .getInvestmentOptions()
      .then((types) => {
        if (active && Array.isArray(types) && types.length) setInvestmentTypes(types);
      })
      .catch(() => {
        /* keep defaults */
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (editing && initialData) {
      setFormData({
        name: initialData.name || '',
        investment_type: initialData.investment_type || 'Stocks',
        custom_type: initialData.custom_type ?? '',
        platform: initialData.platform ?? '',
        remarks: initialData.remarks ?? '',
        initial_amount: '',
        start_date: '',
      });
    } else {
      setFormData({
        name: '',
        investment_type: 'Stocks',
        custom_type: '',
        platform: '',
        remarks: '',
        initial_amount: '',
        start_date: '',
      });
    }
  }, [editing, initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      name: formData.name,
      investment_type: formData.investment_type,
      custom_type:
        formData.investment_type === 'Other' ? formData.custom_type || null : null,
      platform: formData.platform || null,
      remarks: formData.remarks || null,
    };
    // Initial contribution + start date are create-only (cost basis grows via
    // contributions afterwards).
    if (!editing) {
      payload.initial_amount = formData.initial_amount
        ? parseFloat(formData.initial_amount)
        : null;
      payload.start_date = formData.start_date || null;
    }
    onSubmit(payload);
    onClose();
  };

  return (
    <ModalBackdrop onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{editing ? 'Edit Investment' : 'Add Investment'}</ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label="Close">
            ×
          </CloseButton>
        </ModalHeader>

        <FormWrapper onSubmit={handleSubmit}>
          <FormRow>
            <Label htmlFor="name">Name*</Label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name ?? ''}
              onChange={handleChange}
              required
              style={inputStyle}
            />
          </FormRow>

          <FormRow>
            <Label htmlFor="investment_type">Type</Label>
            <StatusSelect
              id="investment_type"
              name="investment_type"
              value={formData.investment_type}
              onChange={handleChange}
            >
              {investmentTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </StatusSelect>
          </FormRow>

          {formData.investment_type === 'Other' && (
            <FormRow>
              <Label htmlFor="custom_type">Custom Type</Label>
              <input
                id="custom_type"
                name="custom_type"
                type="text"
                value={formData.custom_type ?? ''}
                onChange={handleChange}
                placeholder="e.g. Collectibles"
                style={inputStyle}
              />
            </FormRow>
          )}

          <FormRow>
            <Label htmlFor="platform">Platform</Label>
            <input
              id="platform"
              name="platform"
              type="text"
              value={formData.platform ?? ''}
              onChange={handleChange}
              placeholder="e.g. COL Financial, GStocks"
              style={inputStyle}
            />
          </FormRow>

          {!editing && (
            <InstallmentSection>
              <FormRow>
                <Label htmlFor="initial_amount">Initial Amount (optional)</Label>
                <HelperText>Seeds the first contribution</HelperText>
                <BillNumberInput
                  id="initial_amount"
                  name="initial_amount"
                  min="0"
                  value={formData.initial_amount ?? ''}
                  onChange={handleChange}
                  placeholder="e.g. 50000"
                />
              </FormRow>
              <FormRow>
                <Label htmlFor="start_date">Start Date (optional)</Label>
                <HelperText>Date of the initial contribution</HelperText>
                <BillDateInput
                  id="start_date"
                  name="start_date"
                  value={formData.start_date ?? ''}
                  onChange={handleChange}
                />
              </FormRow>
            </InstallmentSection>
          )}

          <FormRow>
            <Label htmlFor="remarks">Remarks</Label>
            <textarea
              id="remarks"
              name="remarks"
              value={formData.remarks ?? ''}
              onChange={handleChange}
              rows={3}
              style={inputStyle}
            />
          </FormRow>

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <SubmitButton type="submit">{editing ? 'Save Changes' : 'Add'}</SubmitButton>
            <CancelButton type="button" onClick={onClose}>
              Cancel
            </CancelButton>
          </div>
        </FormWrapper>
      </ModalContent>
    </ModalBackdrop>
  );
};

export default InvestmentFormModal;
